import type { Broadcast, Gift, ActivityItem, Design, DesignRecord, KhutbahLine, KhutbahSettings, SavedTemplate, Schedule, Screen, ScreenBundle, TimetableDay } from '@noorview/core';
import { demoSupport, type SupportApi } from './support';
import { KHUTBAH_SAMPLE, firstWords, wordsOf, LOCKED_MESSAGE, describeMasjidChange, normalizeKhutbah, designLockedBy, isSystemLocked, screenLockedMessage, newId, normalizeDesign, normalizeMasjid, ymd } from '@noorview/core';
import { ConflictError, type Backend, type Change, type NewBroadcast, type DeviceApi, type Member, type Presence, type Session, type Workspace } from './backend';
import { idbClear, idbGet, idbMutate } from './idb';
import { demoTimetable, seedDb, type SandboxDb } from './seed';

/**
 * Sandbox backend: the whole system runs in this browser. Virtual screens opened in other tabs or windows behave like
 * real devices: they register, show a pairing code, receive published designs live and send heartbeats.
 * Behaviour matches the production backend, so everything tested here works the same with Supabase.
 */

const KEY = 'db', PRESENCE = 'presence', UPTIME = 'uptime';
const SESSION_KEY = 'noorview.sandbox.session';
const CHANNEL = 'noorview-sandbox';
const PAIR_CHARS = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // no 0/O or 1/I, so codes are easy to read off a TV

type Msg = Change | { kind: 'device'; deviceId: string };

export class SandboxBackend implements Backend {
  readonly kind = 'sandbox' as const;
  private ch: BroadcastChannel | null = typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL) : null;
  private listeners = new Set<(m: Msg) => void>();

  constructor() {
    this.ch?.addEventListener('message', (e: MessageEvent<Msg>) => this.listeners.forEach((l) => l(e.data)));
  }

  private supportApi: SupportApi | null = null;
  get support() {
    return (this.supportApi ??= demoSupport(() => this.session()?.name ?? 'You', async () => (await this.db()).masjid.plan));
  }

  private emit(m: Msg) {
    this.ch?.postMessage(m);
    this.listeners.forEach((l) => l(m));
  }

  private async db(): Promise<SandboxDb> {
    const d = await idbGet<SandboxDb>(KEY);
    // Sandboxes from before times came only from the timetable get the example timetable once.
    if (d && !d.ttSeeded) return idbMutate<SandboxDb, SandboxDb>(KEY, (cur) => {
      const v = cur ?? seedDb();
      if (!v.ttSeeded) { if (!v.timetable.length) v.timetable = demoTimetable(v.masjid, ymd(new Date(), v.masjid.timezone)); v.ttSeeded = true; }
      return { value: v, result: v };
    });
    if (d) return d;
    return idbMutate<SandboxDb, SandboxDb>(KEY, (cur) => { const v = cur ?? seedDb(); return { value: v, result: v }; });
  }

  /** Changes are refused while the system is locked (only unlocking is allowed). */
  private guard(db: SandboxDb) { if (isSystemLocked(db.screens)) throw new Error(LOCKED_MESSAGE); }
  /** The same lockdown as the database (migration 0015): a locked screen, or a design a locked screen shows, can’t change. */
  private guardScreens(db: SandboxDb, ids: Array<string | null | undefined>) {
    const s = db.screens.find((x) => x.locked && ids.includes(x.id));
    if (s) throw new Error(screenLockedMessage(s.name));
  }
  private guardDesign(db: SandboxDb, designId: string) {
    const names = designLockedBy(db.screens, designId);
    if (names) throw new Error(screenLockedMessage(names));
  }

  private async mutate<R>(fn: (db: SandboxDb) => R, change: Msg = { kind: 'workspace' }): Promise<R> {
    await this.db();
    const r = await idbMutate<SandboxDb, R>(KEY, (cur) => {
      const db = cur ?? seedDb();
      const result = fn(db);
      return { value: db, result };
    });
    this.emit(change);
    return r;
  }

  private log(db: SandboxDb, kind: ActivityItem['kind'], title: string, detail: string) {
    db.activity.unshift({ id: newId('a'), masjidId: db.masjid.id, kind, title, detail, at: new Date().toISOString(), read: false });
    db.activity = db.activity.slice(0, 200);
  }

  private who(): string { return this.session()?.name ?? 'Someone'; }

  /* ---------- auth ---------- */

  ready() { return Promise.resolve(); }

  session(): Session | null {
    try { const s = localStorage.getItem(SESSION_KEY); return s ? (JSON.parse(s) as Session) : null; } catch { return null; }
  }

  async signIn(email: string, password: string): Promise<Session> {
    const e = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(e)) throw new Error('Enter your email address.');
    if (password.length < 1) throw new Error('Enter your password.');
    const db = await this.db();
    const member = db.members.find((m) => m.email === e) ?? db.members[0]!;
    const name = member.email === e ? member.name : e.split('@')[0]!.replace(/^\w/, (c) => c.toUpperCase());
    const s: Session = { userId: member.id, name, email: e, role: member.role };
    localStorage.setItem(SESSION_KEY, JSON.stringify(s));
    return s;
  }

  /** Set when the sandbox is the demo of a real deployment: called after signing out. */
  onSignedOut?: () => void;
  async signOut() { localStorage.removeItem(SESSION_KEY); this.onSignedOut?.(); }
  async requestPasswordReset() { /* the sandbox has no email */ }

  /** Wipes the sandbox back to its starting data. */
  async reset() { await idbClear(); this.emit({ kind: 'workspace' }); }

  /* ---------- workspace ---------- */

  async load(): Promise<Workspace> {
    const db = await this.db();
    const presence = Object.values((await idbGet<Record<string, Presence>>(PRESENCE)) ?? {});
    const screens = db.screens.map((s) => {
      const p = presence.filter((x) => x.screenId === s.id).sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt))[0];
      return p ? { ...s, lastSeenAt: p.lastSeenAt, showingVersion: p.version } : s;
    });
    // Designs saved by an older version are brought up to date (e.g. onto the fixed grid) as they're read.
    const designs = db.designs.map((d) => ({ ...d, draft: normalizeDesign(d.draft), published: d.published ? normalizeDesign(d.published) : d.published }));
    return { masjid: { ...normalizeMasjid(db.masjid), khutbahAddon: true }, screens, designs, schedules: db.schedules, timetable: db.timetable, activity: db.activity, members: db.members, presence,
      templates: (db.templates ?? []).map((t) => ({ ...t, design: normalizeDesign(t.design) })),
      broadcasts: (db.broadcasts ?? []).filter((b) => !b.endsAt || Date.parse(b.endsAt) > Date.now() - 7 * 86_400_000),
      uptime: Object.entries((await idbGet<Record<string, number>>(UPTIME)) ?? {}).map(([k, seconds]) => { const [screenId, day] = k.split('|'); return { screenId: screenId!, day: day!, seconds }; }) };
  }

  subscribe(cb: (c: Change) => void) {
    const l = (m: Msg) => { if (m.kind !== 'device') cb(m); };
    this.listeners.add(l);
    return () => { this.listeners.delete(l); };
  }

  async updateMasjid(patch: Partial<Omit<SandboxDb['masjid'], 'id'>>) {
    await this.mutate((db) => {
      this.guard(db);
      const before = db.masjid;
      db.masjid = normalizeMasjid({ ...db.masjid, ...patch, prayer: { ...db.masjid.prayer, ...(patch.prayer ?? {}) } });
      const what = describeMasjidChange(before, db.masjid);
      if (what) this.log(db, what.kind, what.title, `${this.who()} · All screens`);
    });
    this.notifyAllDevices();
  }

  async createScreen({ name, design }: { name: string; design: Design }): Promise<Screen> {
    return this.mutate((db) => {
      this.guard(db);
      const at = new Date().toISOString();
      const d: DesignRecord = { id: newId('d'), masjidId: db.masjid.id, name, draft: normalizeDesign(design), draftRevision: 1, draftUpdatedAt: at, publishedVersion: 0, published: null, publishedAt: null };
      const s: Screen = { id: newId('s'), masjidId: db.masjid.id, name: name.slice(0, 40), designId: d.id, deviceId: null, power: 'on', locked: false, lastSeenAt: null, showingVersion: null, createdAt: at };
      db.designs.push(d); db.screens.push(s);
      return s;
    });
  }

  async updateScreen(id: string, patch: Partial<Pick<Screen, 'name' | 'power' | 'locked' | 'designId' | 'salahBlank'>>) {
    await this.updateScreens([id], patch);
  }

  async updateScreens(ids: string[], patch: Partial<Pick<Screen, 'name' | 'power' | 'locked' | 'designId' | 'salahBlank'>>) {
    const devices = await this.mutate((db) => {
      if (Object.keys(patch).some((k) => k !== 'locked')) { this.guard(db); if (patch.locked !== false) this.guardScreens(db, ids); }
      const out: string[] = [];
      for (const s of db.screens) if (ids.includes(s.id)) {
        if (patch.name !== undefined) s.name = patch.name.slice(0, 40);
        if (patch.power) s.power = patch.power;
        if (patch.locked !== undefined) s.locked = patch.locked;
        if (patch.salahBlank !== undefined) s.salahBlank = patch.salahBlank;
        if (patch.designId && db.designs.some((d) => d.id === patch.designId)) s.designId = patch.designId;
        if (s.deviceId) out.push(s.deviceId);
      }
      return out;
    });
    devices.forEach((d) => this.emit({ kind: 'device', deviceId: d }));
  }

  async deleteScreen(id: string) {
    const device = await this.mutate((db) => {
      this.guard(db); this.guardScreens(db, [id]);
      const s = db.screens.find((x) => x.id === id);
      db.screens = db.screens.filter((x) => x.id !== id);
      db.schedules = db.schedules.filter((x) => x.screenId !== id);
      for (const d of db.devices) if (d.screenId === id) { d.screenId = null; d.code = makeCode(db); }
      // Keep the design if another screen or a schedule still uses it.
      if (s && !db.screens.some((x) => x.designId === s.designId) && !db.schedules.some((x) => x.designId === s.designId)) db.designs = db.designs.filter((d) => d.id !== s.designId);
      return s?.deviceId ?? null;
    });
    if (device) this.emit({ kind: 'device', deviceId: device });
  }

  async pairScreen(code: string, screenId: string) {
    const c = code.toUpperCase().replace(/[^A-Z0-9]/g, '');
    const deviceId = await this.mutate((db) => {
      this.guard(db); this.guardScreens(db, [screenId, db.devices.find((d) => d.code && d.code === c)?.screenId]);
      const dev = db.devices.find((d) => d.code && d.code === c);
      if (!dev) throw new Error('That code doesn’t match a screen. Check the code on the display and try again.');
      const s = db.screens.find((x) => x.id === screenId);
      if (!s) throw new Error('That screen no longer exists.');
      // A screen shows on one device at a time: unpair whatever was there before.
      for (const d of db.devices) if (d.screenId === screenId && d.id !== dev.id) { d.screenId = null; d.code = makeCode(db); }
      dev.screenId = screenId; dev.code = null; dev.pairedAt = new Date().toISOString();
      s.deviceId = dev.id;
      this.log(db, 'paired', `${s.name} paired`, `${this.who()} · New display connected`);
      return dev.id;
    });
    this.emit({ kind: 'device', deviceId });
  }

  async unpairScreen(screenId: string) {
    const deviceId = await this.mutate((db) => {
      this.guard(db); this.guardScreens(db, [screenId]);
      const s = db.screens.find((x) => x.id === screenId);
      const dev = db.devices.find((d) => d.screenId === screenId);
      if (s) s.deviceId = null;
      if (dev) { dev.screenId = null; dev.code = makeCode(db); }
      return dev?.id ?? null;
    });
    if (deviceId) this.emit({ kind: 'device', deviceId });
  }

  /* ---------- designs ---------- */

  async saveDraft(designId: string, draft: Design, baseRevision: number, force = false) {
    const clean = normalizeDesign(draft);
    let latest: DesignRecord | null = null;
    const r = await this.mutate((db) => {
      this.guard(db); this.guardDesign(db, designId);
      const d = db.designs.find((x) => x.id === designId);
      if (!d) throw new Error('This design was deleted.');
      if (!force && d.draftRevision !== baseRevision) { latest = structuredClone(d); return null; }
      d.draft = clean; d.draftRevision += 1; d.draftUpdatedAt = new Date().toISOString();
      return { revision: d.draftRevision, savedAt: d.draftUpdatedAt };
    }, { kind: 'design', designId, revision: baseRevision + 1, by: this.session()?.userId ?? '' });
    if (!r) throw new ConflictError(latest!);
    return r;
  }

  async publish(designId: string) {
    const { result, devices } = await this.mutate((db) => {
      this.guard(db);
      const d = db.designs.find((x) => x.id === designId);
      if (!d) throw new Error('This design was deleted.');
      const screens = db.screens.filter((s) => s.designId === designId);
      if (screens.length && screens.every((s) => s.locked)) throw new Error(`${screens.map((s) => s.name).join(', ')} ${screens.length > 1 ? 'are' : 'is'} locked. Unlock it to send changes live.`);
      d.published = structuredClone(d.draft); d.publishedVersion += 1; d.publishedAt = new Date().toISOString();
      this.log(db, 'published', `${screens[0]?.name ?? d.name} updated`, `${this.who()} · Version ${d.publishedVersion}`);
      const ids = new Set(screens.filter((s) => !s.locked).map((s) => s.deviceId).filter(Boolean) as string[]);
      for (const sc of db.schedules) if (sc.designId === designId) { const s = db.screens.find((x) => x.id === sc.screenId); if (s?.deviceId && !s.locked) ids.add(s.deviceId); }
      return { result: { version: d.publishedVersion, publishedAt: d.publishedAt }, devices: [...ids] };
    });
    devices.forEach((id) => this.emit({ kind: 'device', deviceId: id }));
    return result;
  }

  async createDesign(name: string, design: Design) {
    return this.mutate((db) => {
      this.guard(db);
      const at = new Date().toISOString();
      const d: DesignRecord = { id: newId('d'), masjidId: db.masjid.id, name: name.slice(0, 40), draft: normalizeDesign(design), draftRevision: 1, draftUpdatedAt: at, publishedVersion: 0, published: null, publishedAt: null };
      db.designs.push(d);
      return d;
    });
  }

  async renameDesign(id: string, name: string) { await this.mutate((db) => { this.guard(db); this.guardDesign(db, id); const d = db.designs.find((x) => x.id === id); if (d) d.name = name.slice(0, 40); }); }

  async deleteDesign(id: string) {
    await this.mutate((db) => {
      this.guard(db); this.guardDesign(db, id);
      if (db.screens.some((s) => s.designId === id)) throw new Error('A screen is still using this design.');
      db.designs = db.designs.filter((d) => d.id !== id);
      db.schedules = db.schedules.filter((s) => s.designId !== id);
    });
  }

  /* ---------- timetable, schedules, activity ---------- */

  async setTimetable(days: TimetableDay[], mode: 'merge' | 'replace' | 'days') {
    await this.mutate((db) => {
      this.guard(db);
      const map = new Map((mode === 'replace' ? [] : db.timetable).map((d) => [d.date, d]));
      // 'merge' adds to a day's times, 'days' replaces each listed day, 'replace' replaces the whole timetable.
      for (const d of days) map.set(d.date, mode === 'merge' ? { ...map.get(d.date), ...d } : d);
      db.timetable = [...map.values()].sort((a, b) => a.date.localeCompare(b.date));
      const first = days[0]?.date, last = days.at(-1)?.date;
      this.log(db, 'timetable', 'Timetable updated', `${this.who()} · ${days.length} day${days.length === 1 ? '' : 's'}${first ? ` (${first} – ${last})` : ''}`);
    });
    this.notifyAllDevices();
  }

  async upsertSchedule(input: Omit<Schedule, 'id' | 'masjidId' | 'createdAt'> & { id?: string }) {
    const s = await this.mutate((db) => {
      this.guard(db); this.guardScreens(db, [input.screenId, input.id ? db.schedules.find((x) => x.id === input.id)?.screenId : null]);
      const ex = input.id ? db.schedules.find((x) => x.id === input.id) : undefined;
      const sc: Schedule = { id: ex?.id ?? newId('sc'), masjidId: db.masjid.id, createdAt: ex?.createdAt ?? new Date().toISOString(), ...input } as Schedule;
      db.schedules = [...db.schedules.filter((x) => x.id !== sc.id), sc];
      const screen = db.screens.find((x) => x.id === sc.screenId);
      this.log(db, 'schedule', `${sc.title} scheduled`, `${this.who()} · ${screen?.name ?? 'Screen'} · ${new Date(sc.startsAt).toLocaleString('en-GB', { weekday: 'short', hour: '2-digit', minute: '2-digit' })}`);
      return sc;
    });
    this.notifyAllDevices();
    return s;
  }

  async saveTemplate(name: string, design: Design) {
    return this.mutate((db) => {
      this.guard(db);
      const t: SavedTemplate = { id: newId('t'), masjidId: db.masjid.id, name: name.trim().slice(0, 40) || 'My template', design: normalizeDesign(design), createdAt: new Date().toISOString() };
      db.templates = [...(db.templates ?? []), t].slice(-60);
      return t;
    });
  }
  /* ---------- live messages and appeals ---------- */

  async startBroadcast(b: NewBroadcast): Promise<Broadcast> {
    const out = await this.mutate((db) => {
      this.guard(db);
      const rec: Broadcast = { ...structuredClone(b), id: newId('b'), masjidId: db.masjid.id, createdAt: new Date().toISOString() };
      db.broadcasts = [...(db.broadcasts ?? []).filter((x) => !x.endsAt || Date.parse(x.endsAt) > Date.now() - 7 * 86_400_000), rec];
      const where = b.screenIds ? `${b.screenIds.length} screen${b.screenIds.length === 1 ? '' : 's'}` : 'All screens';
      this.log(db, b.kind, b.kind === 'appeal' ? 'Live appeal started' : `${b.message?.heading || 'Message'} sent to screens`, `${this.who()} · ${where}`);
      return rec;
    });
    this.notifyAllDevices();
    return out;
  }
  private async editBroadcast(id: string, fn: (b: Broadcast) => void) {
    await this.mutate((db) => {
      this.guard(db);
      const b = (db.broadcasts ?? []).find((x) => x.id === id);
      if (!b) throw new Error('That message has already gone.');
      fn(b);
    });
    this.notifyAllDevices();
  }
  async updateBroadcast(id: string, patch: Parameters<Backend['updateBroadcast']>[1]) {
    await this.editBroadcast(id, (b) => {
      const { target, title, ...rest } = patch;
      Object.assign(b, rest);
      if (b.appeal && target != null) b.appeal.target = Math.max(0, target);
      if (b.appeal && title != null) b.appeal.title = title;
    });
  }
  async endBroadcast(id: string) { await this.editBroadcast(id, (b) => { b.endsAt = new Date().toISOString(); }); }

  /* ---------- Live Khutbah (in the sandbox the add-on is on, and the khutbah is the sample, played as if heard) ---------- */

  async saveKhutbah(settings: KhutbahSettings) {
    await this.mutate((db) => {
      this.guard(db);
      const k = normalizeKhutbah(settings);
      if (k.listenerScreenId && !db.screens.some((x) => x.id === k.listenerScreenId)) throw new Error('That screen isn’t in your masjid.');
      db.masjid = { ...db.masjid, khutbah: k };
      for (const b of db.broadcasts ?? []) if (b.kind === 'khutbah' && b.endsAt && Date.parse(b.endsAt) > Date.now()) { b.screenIds = k.screenIds; b.khutbah = { ...b.khutbah!, colours: k.colours, listenerScreenId: k.listenerScreenId ?? b.khutbah!.listenerScreenId }; }
    });
    this.notifyAllDevices();
  }
  async startKhutbah(minutes = 60) {
    await this.mutate((db) => {
      this.guard(db);
      const k = normalizeKhutbah(db.masjid.khutbah);
      if ((db.broadcasts ?? []).some((b) => b.kind === 'khutbah' && b.endsAt && Date.parse(b.endsAt) > Date.now())) return;
      if (!k.listenerScreenId || !db.screens.some((x) => x.id === k.listenerScreenId)) throw new Error('Choose the screen that listens to the khutbah first.');
      const now = new Date();
      const rec: Broadcast = { id: newId('b'), masjidId: db.masjid.id, kind: 'khutbah', screenIds: k.screenIds, placement: 'full', startsAt: now.toISOString(), endsAt: new Date(now.getTime() + Math.min(180, Math.max(5, minutes)) * 60_000).toISOString(), createdAt: now.toISOString(),
        khutbah: { listenerScreenId: k.listenerScreenId, colours: k.colours, startedBy: this.who() } };
      db.broadcasts = [...(db.broadcasts ?? []), rec];
      this.log(db, 'khutbah', 'Live khutbah started', `${this.who()} · ${k.screenIds ? `${k.screenIds.length} screen(s)` : 'All screens'}`);
    });
    this.notifyAllDevices();
  }
  async stopKhutbah() {
    await this.mutate((db) => {
      let ended = false;
      for (const b of db.broadcasts ?? []) if (b.kind === 'khutbah' && b.endsAt && Date.parse(b.endsAt) > Date.now()) { b.endsAt = new Date().toISOString(); ended = true; }
      if (ended) this.log(db, 'khutbah', 'Live khutbah ended', this.who());
    });
    this.notifyAllDevices();
  }
  async khutbahLines(broadcastId: string) { return sampleLines((await this.db()).broadcasts?.find((b) => b.id === broadcastId)?.startsAt, Date.now()); }
  watchKhutbah(broadcastId: string, cb: (lines: KhutbahLine[]) => void) { return this.playSample(broadcastId, cb); }

  /** The sample khutbah as if it were being heard now: a sentence every 9 seconds, its English a moment later. */
  private playSample(broadcastId: string, cb: (lines: KhutbahLine[]) => void) {
    const seen = new Map<number, string>();
    let alive = true;
    const run = async () => {
      const b = (await this.db()).broadcasts?.find((x) => x.id === broadcastId);
      const fresh = sampleLines(b?.startsAt, Date.now()).filter((l) => { const k = JSON.stringify(l); if (seen.get(l.seq) === k) return false; seen.set(l.seq, k); return true; });
      if (fresh.length && alive) cb(fresh);
    };
    void run();
    const t = setInterval(() => void run(), 1000);
    return () => { alive = false; clearInterval(t); };
  }
  async addGift(id: string, gift: Omit<Gift, 'id' | 'at'>) {
    if (!(gift.amount > 0)) throw new Error('Enter an amount.');
    await this.editBroadcast(id, (b) => { if (b.appeal) b.appeal.gifts.push({ ...gift, amount: Math.round(gift.amount * 100) / 100, name: gift.name.slice(0, 60), id: newId('g'), at: new Date().toISOString() }); });
  }
  async removeGift(id: string, giftId: string) { await this.editBroadcast(id, (b) => { if (b.appeal) b.appeal.gifts = b.appeal.gifts.filter((g) => g.id !== giftId); }); }

  async renameTemplate(id: string, name: string) { await this.mutate((db) => { this.guard(db); db.templates = (db.templates ?? []).map((t) => (t.id === id ? { ...t, name: name.trim().slice(0, 40) || t.name } : t)); }); }
  async deleteTemplate(id: string) { await this.mutate((db) => { this.guard(db); db.templates = (db.templates ?? []).filter((t) => t.id !== id); }); }

  async deleteSchedule(id: string) { await this.mutate((db) => { this.guard(db); this.guardScreens(db, [db.schedules.find((x) => x.id === id)?.screenId]); db.schedules = db.schedules.filter((s) => s.id !== id); }); this.notifyAllDevices(); }

  async markActivityRead(ids?: string[]) {
    await this.mutate((db) => { for (const a of db.activity) if (!ids || ids.includes(a.id)) a.read = true; });
  }

  async uploadMedia(file: Blob) {
    return new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(String(r.result));
      r.onerror = () => reject(r.error ?? new Error('Couldn’t read the file.'));
      r.readAsDataURL(file);
    });
  }

  async inviteMember(email: string, role: Member['role']) {
    const e = email.trim().toLowerCase();
    if (!/^\S+@\S+\.\S+$/.test(e)) throw new Error('Enter a valid email address.');
    await this.mutate((db) => {
      if (db.members.some((m) => m.email === e)) throw new Error('That person is already on your team.');
      db.members.push({ id: newId('u'), name: e.split('@')[0]!, email: e, role, joinedAt: new Date().toISOString() });
    });
  }

  /** As the real service: hand over if you're the owner, close the (sandbox) account if you're the last one, sign out. */
  async leaveMasjid(newOwnerId?: string | null) {
    const me = this.session()?.userId;
    const db = await this.db();
    const mine = db.members.find((m) => m.id === me) ?? db.members.find((m) => m.role === 'owner');
    if (!mine) throw new Error('You’re not on this masjid’s team.');
    const others = db.members.filter((m) => m.id !== mine.id);
    if (!others.length) { await this.reset(); await this.signOut(); return { closed: true }; }
    if (mine.role === 'owner' && !others.some((m) => m.id === newOwnerId)) throw new Error('Choose who will own the masjid after you leave.');
    await this.mutate((d) => {
      if (mine.role === 'owner') d.members = d.members.map((m) => (m.id === newOwnerId ? { ...m, role: 'owner' as const } : m));
      d.members = d.members.filter((m) => m.id !== mine.id);
    });
    await this.signOut();
    return { closed: false };
  }

  async removeMember(id: string) {
    await this.mutate((db) => {
      const m = db.members.find((x) => x.id === id);
      if (m?.role === 'owner') throw new Error('The owner can’t be removed.');
      db.members = db.members.filter((x) => x.id !== id);
    });
  }

  async updateMemberRole(id: string, role: Member['role']) {
    if (role === 'owner') throw new Error('There can only be one owner.');
    await this.mutate((db) => {
      const m = db.members.find((x) => x.id === id);
      if (!m) throw new Error('That person isn’t on your team any more.');
      if (m.role === 'owner') throw new Error('The owner’s role can’t be changed.');
      m.role = role;
    });
  }

  private notifyAllDevices() {
    void this.db().then((db) => db.devices.forEach((d) => d.screenId && this.emit({ kind: 'device', deviceId: d.id })));
  }

  /* ---------- device ---------- */

  device: DeviceApi = {
    register: async (existingId) => {
      return this.mutate((db) => {
        let dev = existingId ? db.devices.find((d) => d.id === existingId) : undefined;
        if (!dev) { dev = { id: newId('dev'), code: makeCode(db), screenId: null, createdAt: new Date().toISOString() }; db.devices.push(dev); }
        if (!dev.screenId && !dev.code) dev.code = makeCode(db);
        return { deviceId: dev.id, code: dev.code, screenId: dev.screenId };
      }, { kind: 'presence' });
    },
    bundle: async (deviceId) => {
      const db = await this.db();
      const dev = db.devices.find((d) => d.id === deviceId);
      const screen = dev?.screenId ? db.screens.find((s) => s.id === dev.screenId) : undefined;
      if (!dev || !screen) return null;
      const rec = db.designs.find((d) => d.id === screen.designId);
      if (!rec) return null;
      const pick = (d: DesignRecord) => normalizeDesign(d.published ?? d.draft);
      const bundle: ScreenBundle = {
        screen: { id: screen.id, name: screen.name, power: screen.power, locked: screen.locked, salahBlank: screen.salahBlank !== false },
        masjid: { ...db.masjid, khutbahAddon: true },
        version: rec.publishedVersion,
        design: pick(rec),
        schedules: db.schedules.filter((s) => s.screenId === screen.id).flatMap((s) => {
          const d = db.designs.find((x) => x.id === s.designId);
          return d ? [{ id: s.id, startsAt: s.startsAt, durationMinutes: s.durationMinutes, repeatWeekly: s.repeatWeekly, design: pick(d) }] : [];
        }),
        timetable: db.timetable,
        broadcasts: (db.broadcasts ?? []).filter((b) => (!b.endsAt || Date.parse(b.endsAt) > Date.now()) && (!b.screenIds || b.screenIds.includes(screen.id) || b.khutbah?.listenerScreenId === screen.id)),
        generatedAt: new Date().toISOString(),
      };
      return bundle;
    },
    heartbeat: async (deviceId, version) => {
      const db = await this.db();
      const dev = db.devices.find((d) => d.id === deviceId);
      let cameOnline = false, uptimeGap = 0;
      await idbMutate<Record<string, Presence>>(PRESENCE, (cur) => {
        const map = cur ?? {};
        const prev = map[deviceId];
        cameOnline = !!dev?.screenId && (!prev || Date.now() - Date.parse(prev.lastSeenAt) > 60_000);
        map[deviceId] = { deviceId, screenId: dev?.screenId ?? null, lastSeenAt: new Date().toISOString(), version, userAgent: navigator.userAgent.slice(0, 120) };
        // Time on screen: the gap since the last heartbeat, when it was recent (so a sleeping tab isn't counted).
        if (prev && dev?.screenId && prev.screenId === dev.screenId) { const gap = (Date.now() - Date.parse(prev.lastSeenAt)) / 1000; if (gap > 0 && gap <= 30) uptimeGap = gap; }
        return { value: map, result: undefined };
      });
      if (uptimeGap && dev?.screenId) {
        const key = `${dev.screenId}|${ymd(new Date(), db.masjid.timezone)}`;
        const cutoff = ymd(new Date(Date.now() - 31 * 86_400_000), db.masjid.timezone);
        await idbMutate<Record<string, number>>(UPTIME, (cur) => {
          const m = Object.fromEntries(Object.entries(cur ?? {}).filter(([k]) => (k.split('|')[1] ?? '') >= cutoff));
          m[key] = Math.round((m[key] ?? 0) + uptimeGap);
          return { value: m, result: undefined };
        });
      }
      if (cameOnline && dev?.screenId) {
        await this.mutate((d) => { const s = d.screens.find((x) => x.id === dev.screenId); if (s) this.log(d, 'screen-online', `${s.name} came online`, 'Display connected'); });
      }
      this.emit({ kind: 'presence' });
    },
    watch: (deviceId, cb) => {
      const l = (m: Msg) => { if ((m.kind === 'device' && m.deviceId === deviceId) || m.kind === 'workspace') cb(); };
      this.listeners.add(l);
      return () => { this.listeners.delete(l); };
    },
    watchKhutbah: (broadcastId, cb) => this.playSample(broadcastId, cb),
  };

  /** Records that a screen went offline (called by the dashboard when a heartbeat is overdue). Idempotent. */
  async noteOffline(screenId: string, since: string) {
    await this.mutate((db) => {
      const s = db.screens.find((x) => x.id === screenId);
      if (!s) return;
      const last = db.activity.find((a) => (a.kind === 'screen-offline' || a.kind === 'screen-online') && a.title.startsWith(s.name));
      if (last?.kind === 'screen-offline' && last.at >= since) return;
      if (last?.kind === 'screen-offline') return;
      this.log(db, 'screen-offline', `${s.name} went offline`, 'Check power or Wi-Fi');
    });
  }
}

function makeCode(db: SandboxDb): string {
  const used = new Set(db.devices.map((d) => d.code));
  for (;;) {
    const bytes = crypto.getRandomValues(new Uint8Array(6));
    const code = [...bytes].map((b) => PAIR_CHARS[b % PAIR_CHARS.length]).join('');
    if (!used.has(code)) return code;
  }
}

/** The sample khutbah's lines heard by `now`, for one that started at `startsAt` (the last 40). */
function sampleLines(startsAt: string | undefined, now: number): KhutbahLine[] {
  if (!startsAt) return [];
  const t = (now - Date.parse(startsAt)) / 1000;
  const out: KhutbahLine[] = [];
  for (let k = 0; 3 + k * 9 <= t; k++) {
    const s = KHUTBAH_SAMPLE[k % KHUTBAH_SAMPLE.length]!;
    // The English follows the Arabic a few words at a time, as a live khutbah's does.
    const total = wordsOf(s.english ?? []).length, since = t - (4 + k * 9);
    const n = since < 0 ? 0 : Math.min(total, 3 + Math.floor(since / 0.7) * 3);
    out.push({ ...structuredClone(s), seq: k + 1, english: n ? firstWords(s.english ?? [], n) : null, ref: n >= total ? s.ref : null, done: n >= total });
  }
  return out.slice(-40);
}
